import React from "react";
import { PageSection } from "@/components/shared";
import { Factory, Layers3, Handshake, ShieldCheck, TrendingUp, TrendingDown, ClipboardList, PackageCheck, User, Timer, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { formatDate } from "@/lib/datetime";

// ---------------- MOCK DATA (functional wiring later) ----------------

const MOCK_L1_ONPROCESS = [
  { batch_no: "FAB-2026-00042", rm: "Rg 24 Gopi N", worker: "Kannan V", issued: 5,   unit: "PCS", started: "2026-08-06T09:12:00" },
  { batch_no: "FAB-2026-00041", rm: "Rg 18 96 Ml",  worker: "Suresh R",  issued: 12,  unit: "PCS", started: "2026-08-06T08:40:00" },
  { batch_no: "FAB-2026-00040", rm: "Rg Ay 900-2.10", worker: "Vinoth K", issued: 3, unit: "PCS", started: "2026-08-05T16:30:00" },
  { batch_no: "FAB-2026-00039", rm: "Rg 1418 Roll", worker: "Malar S",   issued: 8,   unit: "PCS", started: "2026-08-05T14:15:00" },
];

const MOCK_SEMI_STOCK = [
  { rm: "Rg 24 Gopi N",     qty: 320, unit: "PCS", trend: "up",   delta: "+40" },
  { rm: "Rg 18 96 Ml",       qty: 180, unit: "PCS", trend: "up",   delta: "+15" },
  { rm: "Rg 1418 Roll",     qty: 96,  unit: "PCS", trend: "down", delta: "-12" },
  { rm: "Rg Ay 900-2.10",   qty: 54,  unit: "PCS", trend: "flat", delta: "0" },
  { rm: "Rg 1534 Roll",     qty: 42,  unit: "PCS", trend: "up",   delta: "+8" },
];

const MOCK_L2_ONPROCESS = [
  { batch_no: "PLT-2026-00018", rm: "Rg 24 Gopi N", worker: "Preethi M", issued: 100, unit: "PCS", started: "2026-08-06T10:05:00" },
  { batch_no: "PLT-2026-00017", rm: "Rg 18 Majaa M", worker: "Divya P",  issued: 60,  unit: "PCS", started: "2026-08-06T09:20:00" },
  { batch_no: "PLT-2026-00016", rm: "Rg 1534 Roll",  worker: "Kavya R",  issued: 40,  unit: "PCS", started: "2026-08-05T15:45:00" },
];

const MOCK_READY_FOR_QC = [
  { rm: "Rg 18 Majaa M", qty: 20, unit: "PCS", mapped_product: "Majaa Bangles Set" },
  { rm: "Rg 24 Gopi N",  qty: 15, unit: "PCS", mapped_product: "CZ JIMIKI 559 RG" },
  { rm: "Rg 18 333",     qty: 8,  unit: "PCS", mapped_product: "Rg 18 333 Chain" },
  { rm: "RG SINGA M ROLL", qty: 12, unit: "PCS", mapped_product: "Singa Chain Set" },
  { rm: "Rg 1418 Roll",  qty: 6,  unit: "PCS", mapped_product: "1418 Chain Basic" },
];

const MOCK_TODAY = {
  l1_issued:  { qty: 28, batches: 4 },
  l1_received:{ qty: 640, batches: 3 },
  l2_issued:  { qty: 200, batches: 3 },
  l2_received:{ qty: 165, batches: 2, damaged: 8, lost: 2 },
  qc_passed:  { qty: 145, events: 5 },
  qc_failed:  { qty: 12,  events: 3, top_reason: "Damaged" },
};

// ---------------- COMPONENT ----------------

export default function ProductionMonitor() {
  return (
    <div className="space-y-6 pb-8">
      <PageSection
        title="Production Monitor"
        description="Live pulse of the shop floor — issued, waiting, and today's throughput at a glance"
        action={<LiveBadge />}
      />

      {/* ==================== TODAY STATS TILES ==================== */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <StatTile
          label="L1 Issued Today"
          value={MOCK_TODAY.l1_issued.qty}
          sub={`${MOCK_TODAY.l1_issued.batches} batches issued`}
          icon={Factory}
          gradient="from-rose-500 to-red-700"
          shadow="shadow-rose-200"
          trend="up"
        />
        <StatTile
          label="L1 Received Today"
          value={MOCK_TODAY.l1_received.qty}
          sub={`${MOCK_TODAY.l1_received.batches} batches yielded ${MOCK_TODAY.l1_received.qty} PCS`}
          icon={PackageCheck}
          gradient="from-emerald-500 to-emerald-700"
          shadow="shadow-emerald-200"
          trend="up"
        />
        <StatTile
          label="L2 Issued Today"
          value={MOCK_TODAY.l2_issued.qty}
          sub={`${MOCK_TODAY.l2_issued.batches} plating batches out`}
          icon={Handshake}
          gradient="from-indigo-500 to-indigo-700"
          shadow="shadow-indigo-200"
          trend="up"
        />
        <StatTile
          label="L2 Received Today"
          value={MOCK_TODAY.l2_received.qty}
          sub={<>
            Recv {MOCK_TODAY.l2_received.qty} · <span className="text-amber-200">Dmg {MOCK_TODAY.l2_received.damaged}</span> · <span className="text-rose-200">Lost {MOCK_TODAY.l2_received.lost}</span>
          </>}
          icon={PackageCheck}
          gradient="from-cyan-500 to-teal-700"
          shadow="shadow-cyan-200"
          trend="up"
        />
        <StatTile
          label="QC Passed Today"
          value={MOCK_TODAY.qc_passed.qty}
          sub={`${MOCK_TODAY.qc_passed.events} QC events`}
          icon={ShieldCheck}
          gradient="from-lime-500 to-green-700"
          shadow="shadow-lime-200"
          trend="up"
        />
        <StatTile
          label="QC Failed Today"
          value={MOCK_TODAY.qc_failed.qty}
          sub={`${MOCK_TODAY.qc_failed.events} events · top: ${MOCK_TODAY.qc_failed.top_reason}`}
          icon={TrendingDown}
          gradient="from-orange-500 to-red-600"
          shadow="shadow-orange-200"
          trend="down"
        />
      </div>

      {/* ==================== 4 WIDGET LISTS ==================== */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Widget
          title="L1 Fabrication — On Process"
          subtitle="Issued raw materials waiting to return as semi-finished"
          icon={Factory}
          count={MOCK_L1_ONPROCESS.length}
          accent="rose"
        >
          <IssuedTable rows={MOCK_L1_ONPROCESS} tone="rose" />
        </Widget>

        <Widget
          title="Semi-Finished Stock"
          subtitle="RG-based semi stock, ready to enter L2 plating"
          icon={Layers3}
          count={MOCK_SEMI_STOCK.length}
          accent="amber"
        >
          <SemiStockTable rows={MOCK_SEMI_STOCK} />
        </Widget>

        <Widget
          title="L2 Plating — On Process"
          subtitle="Semi stock issued to platers, awaiting finished return"
          icon={Handshake}
          count={MOCK_L2_ONPROCESS.length}
          accent="indigo"
        >
          <IssuedTable rows={MOCK_L2_ONPROCESS} tone="indigo" />
        </Widget>

        <Widget
          title="Ready for Quality Control"
          subtitle="Finished items awaiting inspection before warehousing"
          icon={ShieldCheck}
          count={MOCK_READY_FOR_QC.length}
          accent="emerald"
        >
          <ReadyForQCTable rows={MOCK_READY_FOR_QC} />
        </Widget>
      </div>

      <div className="text-[11px] text-gray-400 text-center pt-2 uppercase tracking-widest">
        Widgets show mock data · live wiring coming next iteration
      </div>
    </div>
  );
}

// ---------------- Sub-components ----------------

function LiveBadge() {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600"></span>
      </span>
      <span className="text-[11px] uppercase tracking-widest font-semibold text-emerald-700">Live · {formatDate(new Date())}</span>
    </div>
  );
}

function StatTile({ label, value, sub, icon: Icon, gradient, shadow, trend }) {
  const TrendIcon = trend === "up" ? ArrowUpRight : trend === "down" ? ArrowDownRight : null;
  return (
    <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${gradient} p-5 text-white ${shadow} shadow-lg`}>
      {/* Decorative background icon */}
      <Icon className="absolute -right-3 -bottom-3 h-24 w-24 text-white/10" />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-white/80">{label}</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-4xl font-heading font-bold tabular-nums">{value.toLocaleString("en-IN")}</span>
            {TrendIcon && <TrendIcon className="h-5 w-5 opacity-80" />}
          </div>
          <div className="text-xs text-white/90 mt-1">{sub}</div>
        </div>
        <div className="rounded-lg bg-white/20 backdrop-blur-sm p-2">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

const ACCENT = {
  rose:    { bar: "from-rose-500 to-red-600",       text: "text-rose-700",    bg: "bg-rose-50",    border: "border-rose-200"    },
  amber:   { bar: "from-amber-500 to-orange-500",   text: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-200"   },
  indigo:  { bar: "from-indigo-500 to-violet-600",  text: "text-indigo-700",  bg: "bg-indigo-50",  border: "border-indigo-200"  },
  emerald: { bar: "from-emerald-500 to-teal-600",   text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
};

function Widget({ title, subtitle, icon: Icon, count, accent, children }) {
  const A = ACCENT[accent];
  return (
    <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
      <div className={`h-1 bg-gradient-to-r ${A.bar}`} />
      <div className="p-4 flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg ${A.bg} ${A.border} border flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-5 w-5 ${A.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-semibold text-gray-900 truncate">{title}</h3>
            <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${A.bg} ${A.text} ${A.border} border tabular-nums`}>{count}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="border-t border-gray-100 max-h-80 overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

function IssuedTable({ rows, tone }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 sticky top-0">
        <tr className="text-left text-[10px] uppercase tracking-widest text-gray-500">
          <th className="px-4 py-2">Batch</th>
          <th className="px-3 py-2">RM</th>
          <th className="px-3 py-2">Worker</th>
          <th className="px-3 py-2 text-right">Issued</th>
          <th className="px-3 py-2">Started</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((r) => (
          <tr key={r.batch_no} className="hover:bg-gray-50">
            <td className={`px-4 py-2 font-mono text-xs font-semibold ${ACCENT[tone].text}`}>{r.batch_no}</td>
            <td className="px-3 py-2 text-xs font-medium">{r.rm}</td>
            <td className="px-3 py-2 text-xs">
              <span className="inline-flex items-center gap-1 text-gray-600"><User className="h-3 w-3" />{r.worker}</span>
            </td>
            <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold">{r.issued} <span className="text-[10px] text-gray-400 font-normal">{r.unit}</span></td>
            <td className="px-3 py-2 text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1"><Timer className="h-3 w-3" />{relTime(r.started)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SemiStockTable({ rows }) {
  const max = Math.max(...rows.map((r) => r.qty), 1);
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 sticky top-0">
        <tr className="text-left text-[10px] uppercase tracking-widest text-gray-500">
          <th className="px-4 py-2">Raw Material</th>
          <th className="px-3 py-2 text-right w-20">Qty</th>
          <th className="px-3 py-2">Stock Level</th>
          <th className="px-3 py-2 text-right w-16">Δ</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((r) => {
          const pct = Math.max(6, Math.round((r.qty / max) * 100));
          const deltaColor = r.trend === "up" ? "text-emerald-600" : r.trend === "down" ? "text-red-600" : "text-gray-400";
          return (
            <tr key={r.rm} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-xs font-medium">{r.rm}</td>
              <td className="px-3 py-2 text-right tabular-nums text-sm font-bold">{r.qty}</td>
              <td className="px-3 py-2">
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: `${pct}%` }} />
                </div>
              </td>
              <td className={`px-3 py-2 text-right text-xs font-bold tabular-nums ${deltaColor}`}>{r.delta}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ReadyForQCTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 sticky top-0">
        <tr className="text-left text-[10px] uppercase tracking-widest text-gray-500">
          <th className="px-4 py-2">Finished Item (RM)</th>
          <th className="px-3 py-2">Mapped Product</th>
          <th className="px-3 py-2 text-right">Available</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((r) => (
          <tr key={r.rm} className="hover:bg-gray-50">
            <td className="px-4 py-2 text-xs font-medium">
              {r.rm}
              <span className="ml-1 text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5 font-semibold">Finished</span>
            </td>
            <td className="px-3 py-2 text-xs text-gray-600 italic">→ {r.mapped_product}</td>
            <td className="px-3 py-2 text-right tabular-nums text-sm font-bold text-emerald-700">{r.qty} <span className="text-[10px] text-gray-400 font-normal">{r.unit}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------- utils ----------------

function relTime(iso) {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.floor((now - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
